/**
 * graspVisualizer
 * ---------------
 * Creates and updates Three.js overlay objects that show grasp candidate
 * poses on top of scene objects. Each candidate is rendered as a U-shaped
 * wireframe gripper glyph, coloured by score.
 *
 * Coordinate conventions
 * ----------------------
 * - Grasps are stored in object-local Z-up frame (MuJoCo convention).
 * - Three.js scene is Y-up.
 * - The overlay group's quaternion is copied from the poseGroup's world
 *   quaternion, which already includes the modelGroup's Rx(-pi/2) rotation
 *   (Z-up -> Y-up). Therefore glyph vertices must be expressed in Z-up
 *   body-local frame.
 */

import * as THREE from "three";
import type { GraspCandidate } from "../../types/grasps";

const OVERLAY_NAME_PREFIX = "__graspOverlay_";
const TOP_K_VISIBLE = 10;
const SELECTED_GRASP_COLOR = new THREE.Color("#00b8ff");
// Molmo grasp frame appears rolled relative to our visual glyph convention.
// Applying a fixed local roll aligns bracket orientation with demo previews.
const GLYPH_ROLL_OFFSET_RAD = Math.PI / 2;

const JAW_HALF_WIDTH = 0.02;
// Keep the glyph compact and centered around the grasp origin so it appears
// as a bracket hugging the object rather than floating behind it.
const FINGER_DEPTH = 0.018;
const BRACKET_RADIUS = 0.0012;
type DrawableObject = THREE.Object3D & {
  geometry?: THREE.BufferGeometry;
  material?: THREE.Material | THREE.Material[];
};
type ColorableMaterial = THREE.Material & { color?: THREE.Color };

function scoreToColor(score: number): THREE.Color {
  const t = Math.max(0, Math.min(1, score));
  // Quality ramp: red (low/sideways) -> green (high/top-down).
  const low = new THREE.Color("#c62828");
  const high = new THREE.Color("#2e7d32");
  return low.lerp(high, t);
}

function disposeDrawable(obj: THREE.Object3D): void {
  const drawable = obj as DrawableObject;
  drawable.geometry?.dispose?.();
  const material = drawable.material;
  if (Array.isArray(material)) material.forEach((m) => m?.dispose?.());
  else material?.dispose?.();
}

function createRod(start: THREE.Vector3, end: THREE.Vector3, colorHex: number): THREE.Mesh {
  const dir = end.clone().sub(start);
  const length = Math.max(1e-5, dir.length());
  const mid = start.clone().add(end).multiplyScalar(0.5);

  const geometry = new THREE.CylinderGeometry(BRACKET_RADIUS, BRACKET_RADIUS, length, 10);
  const material = new THREE.MeshStandardMaterial({
    color: colorHex,
    roughness: 0.55,
    metalness: 0.05,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(mid);

  // Cylinder axis is +Y by default; rotate it to align with segment direction.
  const yAxis = new THREE.Vector3(0, 1, 0);
  const targetDir = dir.clone().normalize();
  mesh.quaternion.setFromUnitVectors(yAxis, targetDir);
  return mesh;
}

function createGraspGlyph(grasp: GraspCandidate, sourceIndex: number): THREE.Group {
  const glyph = new THREE.Group();

  const [qw, qx, qy, qz] = grasp.orientation;
  const rawQuat = new THREE.Quaternion(qx, qy, qz, qw);
  const rollOffset = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 0, 1),
    GLYPH_ROLL_OFFSET_RAD
  );
  const graspQuat = rawQuat.clone().multiply(rollOffset);
  const origin = new THREE.Vector3(...grasp.position);

  // U-shaped parallel-jaw gripper in local grasp frame:
  // x = jaw separation axis, z = approach direction.
  // Center the U depth around the grasp origin so the "pinch region" sits near
  // the object instead of behind it.
  const halfDepth = FINGER_DEPTH * 0.5;
  const p1 = new THREE.Vector3(-JAW_HALF_WIDTH, 0, +halfDepth);
  const p2 = new THREE.Vector3(-JAW_HALF_WIDTH, 0, -halfDepth);
  const p3 = new THREE.Vector3(JAW_HALF_WIDTH, 0, -halfDepth);
  const p4 = new THREE.Vector3(JAW_HALF_WIDTH, 0, +halfDepth);

  const transformPoint = (p: THREE.Vector3) => p.clone().applyQuaternion(graspQuat).add(origin);

  const uPoints = [transformPoint(p1), transformPoint(p2), transformPoint(p3), transformPoint(p4)];

  const colorHex = scoreToColor(grasp.score).getHex();
  const rods = [
    createRod(uPoints[0], uPoints[1], colorHex),
    createRod(uPoints[1], uPoints[2], colorHex),
    createRod(uPoints[2], uPoints[3], colorHex),
  ];
  rods.forEach((rod) => {
    rod.userData.isGraspGlyph = true;
    glyph.add(rod);
  });
  glyph.userData.isGraspGlyphRoot = true;
  glyph.userData.sourceIndex = sourceIndex;
  // Store local approach direction for dynamic world-frame recoloring.
  glyph.userData.approachLocal = new THREE.Vector3(0, 0, 1).applyQuaternion(graspQuat).normalize();
  return glyph;
}

function buildGraspGlyphs(group: THREE.Group, grasps: GraspCandidate[]): void {
  while (group.children.length > 0) {
    const child = group.children[0];
    child.traverse((obj) => disposeDrawable(obj));
    group.remove(child);
  }

  for (let i = 0; i < grasps.length; i += 1) {
    group.add(createGraspGlyph(grasps[i], i));
  }
}

export function ensureGraspOverlays(
  scene: THREE.Scene,
  bodyUuid: string,
  grasps: GraspCandidate[]
): THREE.Group {
  const name = `${OVERLAY_NAME_PREFIX}${bodyUuid}`;
  let group = scene.getObjectByName(name) as THREE.Group | undefined;

  if (!group) {
    group = new THREE.Group();
    group.name = name;
    group.userData.isGraspOverlay = true;
    group.userData.bodyUuid = bodyUuid;
    scene.add(group);
  }

  buildGraspGlyphs(group, grasps);
  return group;
}

export function updateGraspOverlayPoses(
  scene: THREE.Scene,
  bodyUuid: string,
  poseGroup: THREE.Object3D,
  options?: {
    selectedSourceIndex?: number | null;
    focusSelectedOnly?: boolean;
    highlightSelected?: boolean;
  }
): void {
  const name = `${OVERLAY_NAME_PREFIX}${bodyUuid}`;
  const group = scene.getObjectByName(name) as THREE.Group | undefined;
  if (!group) return;

  poseGroup.updateWorldMatrix(true, false);
  const worldPos = new THREE.Vector3();
  const worldQuat = new THREE.Quaternion();
  const worldScale = new THREE.Vector3();
  poseGroup.matrixWorld.decompose(worldPos, worldQuat, worldScale);

  group.position.copy(worldPos);
  group.quaternion.copy(worldQuat);

  // Recompute score in world frame each frame, then keep only top-K visible.
  // In scene coordinates Y is up, so top-down approach aligns with -Y.
  const worldDown = new THREE.Vector3(0, -1, 0);
  const scoredGlyphs: Array<{
    glyph: THREE.Object3D;
    worldScore: number;
    sourceIndex: number | null;
  }> = [];

  group.children.forEach((glyph) => {
    const approachLocal = glyph.userData?.approachLocal as THREE.Vector3 | undefined;
    if (!approachLocal) return;
    const approachWorld = approachLocal.clone().applyQuaternion(worldQuat).normalize();
    const worldScore = Math.max(0, Math.min(1, (approachWorld.dot(worldDown) + 1) / 2));
    const sourceIndex = Number.isFinite(Number(glyph.userData?.sourceIndex))
      ? Number(glyph.userData.sourceIndex)
      : null;
    scoredGlyphs.push({ glyph, worldScore, sourceIndex });
  });

  scoredGlyphs.sort((a, b) => b.worldScore - a.worldScore);
  const selectedSourceIndex = Number.isFinite(Number(options?.selectedSourceIndex))
    ? Number(options?.selectedSourceIndex)
    : null;
  const focusSelectedOnly = options?.focusSelectedOnly === true;
  const highlightSelected = options?.highlightSelected !== false;
  let visibleSet: Set<THREE.Object3D>;
  if (focusSelectedOnly && selectedSourceIndex != null) {
    visibleSet = new Set(
      scoredGlyphs
        .filter((entry) => entry.sourceIndex === selectedSourceIndex)
        .map((entry) => entry.glyph)
    );
    if (visibleSet.size === 0 && scoredGlyphs.length > 0) {
      visibleSet = new Set([scoredGlyphs[0].glyph]);
    }
  } else {
    visibleSet = new Set(scoredGlyphs.slice(0, TOP_K_VISIBLE).map((entry) => entry.glyph));
  }

  scoredGlyphs.forEach(({ glyph, worldScore, sourceIndex }) => {
    glyph.visible = visibleSet.has(glyph);
    const isSelected = selectedSourceIndex != null && sourceIndex === selectedSourceIndex;
    const color = isSelected && highlightSelected ? SELECTED_GRASP_COLOR : scoreToColor(worldScore);
    glyph.traverse((obj) => {
      const maybeMat = (obj as DrawableObject).material;
      if (!maybeMat) return;
      const mats = Array.isArray(maybeMat) ? maybeMat : [maybeMat];
      mats.forEach((m) => {
        const colorable = m as ColorableMaterial;
        if (colorable.color) {
          colorable.color.set(color);
        }
      });
    });
  });
}

export function removeGraspOverlays(scene: THREE.Scene, bodyUuid: string): void {
  const name = `${OVERLAY_NAME_PREFIX}${bodyUuid}`;
  const group = scene.getObjectByName(name) as THREE.Group | undefined;
  if (!group) return;

  group.traverse((child) => disposeDrawable(child));
  scene.remove(group);
}

export function removeAllGraspOverlays(scene: THREE.Scene): void {
  const toRemove: THREE.Object3D[] = [];
  scene.traverse((obj) => {
    if (obj.userData?.isGraspOverlay) toRemove.push(obj);
  });
  for (const obj of toRemove) {
    removeGraspOverlays(scene, obj.userData.bodyUuid as string);
  }
}
